/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-multiple-fields-modal/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import cn from 'clsx';
import React, { useEffect, useState } from 'react';
import ForecastEndDateField from '@components/admin/forecast-end-date-field';
import LeadVisitIdField from '@components/admin/lead-visit-id-field';
import PanelConstants from '@components/central-monitoring/panels/add-panel/panel-constants';
import { SaveButton } from '@components/core/buttons';
import CharactersRemaining from '@components/core/characters-remaining';
import CoreConfirmationDialog from '@components/core/core-confirmation-dialog';
import DeleteButton from '@components/core/delete-button';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from '@components/core/dialog';
import DialogInformationBox from '@components/core/dialog-information-box/dialog-information-box';
import Loading from '@components/core/loading';
import SelectWithCategories from '@components/core/select-with-categories';
import SelectWithCategoriesLov from '@components/core/select-with-categories/select-with-categories-lov';
import TextField from '@components/core/text-field';
import {
    fieldsExclusionValidationArray,
    fieldsPack,
    initialFieldsErrors,
    initialsFieldsStatePack,
} from './edit-multiple-fields-modal.constants';
import { EditMultipleFieldsInterface, InitialFieldsStateInterface } from './edit-multiple-fields-modal.interface';
import { useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { createArrayFromEnum } from '@utils/generator';
import NotificationService from '@utils/notification';
import { getLeadVisitField } from '@utils/study-data.service';
import { validateFields } from '@utils/validation';
import { StudyTimeUnit } from '@types/graphql-global-types';
// eslint-disable-next-line max-len
import { StudyVisitScheduleFields_StudySubjectVisitScheduleTemplate_visits_StudySubjectVisit as StudyVisitScheduleFields_StudySubjectVisit } from '@queries/generated/fragments.generated';
import {
    saveStudyVisitScheduleVisits,
    saveStudyVisitScheduleVisitsVariables,
} from '@queries/generated/study-visit-schedules.generated';
import { GET_STUDY_VISIT_SCHEDULES, SAVE_STUDY_VISIT_SCHEDULE_VISITS } from '@queries/study-visit-schedules';

import './styles.scss';

const EditMultipleFieldsModal = ({
    open,
    modalType,
    onClose,
    study,
    visits,
    itemsToUpdateArray,
    currentScheduleId,
}: EditMultipleFieldsInterface) => {
    const { t } = useTranslation();
    const isVisitsModalType = modalType === 'visits';
    const initialState = initialsFieldsStatePack[modalType];
    const initialFieldsToUpdate = fieldsPack[modalType].map((f) => ({ ...f })); // To break a link within the array of objects
    const disabledSave = useBoolean(true);
    const confirmationVisitsOpen = useBoolean(false);
    const noFields = useBoolean(true);
    const leadVisitIdTouched = useBoolean(false);
    const [errors, setErrors] = useState({ ...initialFieldsErrors });

    // @ts-ignore
    const [fieldsState, setFieldsState] = useState<InitialFieldsStateInterface>(initialState);
    const [fieldsToUpdateOptions, setFieldsToUpdateOptions] = useState(initialFieldsToUpdate);
    const [unitOptions, setUnitOptions] = useState<{ id: string; item: string; text: string }[]>([]);
    const [leadVisitSelected, setLeadVisitSelected] = useState<boolean>(false);
    const [saveVisits, { loading: visitsLoading }] = useMutation<
        saveStudyVisitScheduleVisits,
        saveStudyVisitScheduleVisitsVariables
    >(SAVE_STUDY_VISIT_SCHEDULE_VISITS);

    const { endDateUsage } = PanelConstants;

    useEffect(() => {
        if (open) {
            if (isVisitsModalType) {
                const array = createArrayFromEnum(StudyTimeUnit, '_UNIT_VISIT', '');
                const index = array.findIndex((item: any) => item.id === StudyTimeUnit.NONE);
                array.splice(index, 1);
                setUnitOptions(array);
                setLeadVisitSelected(false);
            }
        }
    }, [open]);

    const close = (action?: string | boolean | undefined) => {
        setFieldsToUpdateOptions(initialFieldsToUpdate);
        disabledSave.setTrue();
        onClose(action);
        // @ts-ignore
        setFieldsState(initialState);
        setErrors(initialFieldsErrors);
        setLeadVisitSelected(false);
        noFields.setTrue();
    };

    const saveItems = ({ data }: { data: InitialFieldsStateInterface }) => {
        if (isVisitsModalType) {
            const updatedVisits = itemsToUpdateArray.map((visit: StudyVisitScheduleFields_StudySubjectVisit) => {
                let resetVisitFields = {};
                let latestVisitId = '';

                // Checking that LeadVisitID is selected from fields to update field list and the value is empty
                if (data.leadVisitId === Constants.ObjectId.Empty && leadVisitSelected) {
                    resetVisitFields = {
                        leadPeriod: initialsFieldsStatePack.visits.leadPeriod,
                        leadUnit: initialsFieldsStatePack.visits.leadUnit,
                        minWinPeriod: initialsFieldsStatePack.visits.minWinPeriod,
                        maxWinPeriod: initialsFieldsStatePack.visits.maxWinPeriod,
                        minWinUnit: initialsFieldsStatePack.visits.minWinUnit,
                        isEndDateEarliest: null,
                        forecastEndDateField: '',
                    };
                    latestVisitId = Constants.ObjectId.Empty;
                }
                // Passing the old visit id when LeadVisitID is not selected from fields to update field
                else if (!leadVisitSelected) {
                    latestVisitId = visit.leadVisitId;
                }
                // Getting the lead visit id from the visits list for the selected Visit from fields to update field list
                else {
                    latestVisitId = getLeadVisitField(visits, data?.leadVisitId, 'id', 'visitId');
                }

                return {
                    id: visit.id,
                    active: data?.active ? data.active !== 'false' : visit.active,
                    type: data?.type ?? visit.type,
                    visitId: visit.visitId,
                    visitName: visit.visitName,
                    leadVisitId: latestVisitId,
                    leadPeriod: data?.leadPeriod ?? visit.leadPeriod,
                    leadUnit: data?.leadUnit ?? visit.leadUnit,
                    minWinPeriod: data?.minWinPeriod ?? visit.minWinPeriod,
                    maxWinPeriod: data?.maxWinPeriod ?? visit.maxWinPeriod,
                    minWinUnit: data?.minWinUnit ?? visit.minWinUnit,
                    minOccurrences: data?.minOccurrences ?? visit.minOccurrences,
                    eventIds: visit.eventIds,
                    forecastEndDateField: data?.forecastEndDate
                        ? data.forecastEndDate.map((item: any) => item.id || item.key).join(' | ')
                        : visit.forecastEndDateField,
                    isEndDateEarliest: data?.endDateUsage ? data.endDateUsage === 'EARLIEST' : visit.isEndDateEarliest,
                    sequenceNumber: visit.sequenceNumber,
                    ...resetVisitFields,
                };
            });

            return saveVisits({
                variables: {
                    studyId: study.id,
                    visitScheduleId: currentScheduleId,
                    visits: updatedVisits,
                },
                refetchQueries: [
                    {
                        query: GET_STUDY_VISIT_SCHEDULES,
                        variables: {
                            studyId: study.id,
                        },
                    },
                ],
            });
        }
    };

    const onSubmit = () => {
        const promiseArray: any = [];
        const saveData: any = fieldsState;

        fieldsToUpdateOptions.forEach((field: { id: string; name: string; active: boolean }) => {
            // deleted unused fields
            if (field.active) {
                delete saveData[field.id];
            }
        });

        if (isVisitsModalType) {
            if (!fieldsState.leadVisitId) {
                saveData.leadVisitId = Constants.ObjectId.Empty;
            }
            promiseArray.push(saveItems({ data: saveData }));
        }

        Promise.all(promiseArray)
            .then((res: any) => {
                if (isVisitsModalType) {
                    const countUpdatedVisits = res[0].data.saveStudyVisitScheduleVisits?.length;
                    NotificationService.showNotification(
                        t('admin:successfulUpdateInformationForMultipleVisits', { amount: countUpdatedVisits }),
                    );
                }
                leadVisitIdTouched.setFalse();
                close();
            })
            .catch((error: any) => {
                console.log('catch', error);
                NotificationService.showNotification(t('errorMessage'));
                disabledSave.setFalse();
            });
    };

    const checkForErrors = (validationErrors: any) =>
        Object.keys(validationErrors).filter((item) => !!validationErrors[item])?.length;

    const checkSelectedFields = () => {
        // check if contains field for update
        const hasFieldToUpdate = fieldsToUpdateOptions.findIndex((fieldToUpdate) => !fieldToUpdate.active);

        if (hasFieldToUpdate === -1) {
            disabledSave.setTrue();
            noFields.setTrue();
        } else {
            noFields.setFalse();
        }
    };

    const validateSubmit = () => {
        let validationErrors = { ...errors };
        fieldsToUpdateOptions.forEach((field: { id: string; name: string; active: boolean }) => {
            if (
                !field.active &&
                !fieldsExclusionValidationArray.includes(field.id) &&
                fieldsState[field.id] === initialState[field.id] &&
                field.id !== 'minOccurrences'
            ) {
                validationErrors = {
                    ...validationErrors,
                    [field.id]: t('ra:requiredFieldError'),
                };
            } else if (
                // # of Occurrences has to be at least the initial value or greater
                // and can not be empty so validation has to be handles differently
                !field.active &&
                field.id === 'minOccurrences' &&
                fieldsState.minOccurrences < initialState[field.id]
            ) {
                validationErrors = {
                    ...validationErrors,
                    [field.id]: t('ra:minOccurrencesError'),
                };
            }
        });
        setErrors(validationErrors);

        if (!checkForErrors(validationErrors)) {
            if (isVisitsModalType && leadVisitIdTouched.value) {
                confirmationVisitsOpen.setTrue();

                return;
            }

            onSubmit();
        }
    };

    const handleFieldToUpdateChange = () => (event: any) => {
        const array = [...fieldsToUpdateOptions];
        const currentId = event.target?.value;

        const currentOptionIndex = array.findIndex((fieldToUpdate) => fieldToUpdate.id === currentId);
        array[currentOptionIndex].active = false;

        disabledSave.setFalse();

        if (currentId === 'leadVisitId' && !leadVisitIdTouched.value) {
            leadVisitIdTouched.setTrue();
            setLeadVisitSelected(true);
        }

        setFieldsToUpdateOptions(array);
        checkSelectedFields();
    };

    const deleteField = (id: string) => {
        const array = [...fieldsToUpdateOptions];

        const currentOptionIndex = array.findIndex((fieldToUpdate) => fieldToUpdate.id === id);
        array[currentOptionIndex].active = true;

        if (id === 'leadVisitId') {
            setLeadVisitSelected(false);
        }

        setFieldsState({
            ...fieldsState,
            [id]: initialState[id],
        });

        setErrors({
            ...errors,
            [id]: '',
        });

        disabledSave.setFalse();

        setFieldsToUpdateOptions(array);
        checkSelectedFields();
    };

    const handleChange = (name: string, length?: number, isNumber?: boolean) => (event: any) => {
        const selectedValue = event.target?.value;

        if (isNumber && +selectedValue < 0) return;

        if (isNumber && length && selectedValue.length > length) return;

        disabledSave.setFalse();
        let lengthError = {};

        if (length) {
            lengthError = validateFields(selectedValue, name, length);
        }

        if (name === 'leadVisitId') {
            leadVisitIdTouched.setValue(selectedValue === '');
        }

        setErrors({
            ...errors,
            [name]:
                selectedValue !== initialState[name] || fieldsExclusionValidationArray.includes(name)
                    ? ''
                    : t('ra:requiredFieldError'),
            ...lengthError,
        });

        setFieldsState({
            ...fieldsState,
            [name]: selectedValue,
        });
    };

    const isFieldEnabled = (id: string) => {
        // check if this field has been chosen
        const currentField = fieldsToUpdateOptions?.find((fieldToUpdate) => fieldToUpdate.id === id);

        return currentField && !currentField.active;
    };

    return (
        <>
            {open && confirmationVisitsOpen.value && (
                <CoreConfirmationDialog
                    open={confirmationVisitsOpen.value}
                    close={() => confirmationVisitsOpen.setFalse()}
                    onDecline={() => confirmationVisitsOpen.setFalse()}
                    onConfirm={() => {
                        onSubmit();
                        confirmationVisitsOpen.setFalse();
                    }}
                    title={t('admin:editVisits')}
                    mainText={t('admin:resetBulkVisitsfields')}
                    additionalText={t('common:wouldLikeContinue')}
                    declineBtnText={t('common:cancel')}
                    submitBtnText={t('common:continue')}
                />
            )}
            <CustomDialog
                className="edit-multiple-fields-modal"
                open={open}
                onClose={() => close(false)}
                fullWidth
                scroll="paper"
                maxWidth="md"
            >
                <DialogTitle id="edit-multiple-risks">
                    {isVisitsModalType && t('admin:editVisits')}

                    <FontAwesomeIcon
                        icon={['fal', 'times']}
                        className="fa-lg close-icon-cv"
                        onClick={() => close(false)}
                        data-testid="close-dialog"
                    />
                </DialogTitle>
                <DialogContent style={{ paddingLeft: 15, paddingRight: 15, paddingTop: 10 }} className="warning">
                    <div className="mb-20">
                        {isVisitsModalType && (
                            <DialogInformationBox
                                topText={t('admin:multipleVisitsUpdateInfo', {
                                    visitsNumber: itemsToUpdateArray?.length,
                                })}
                            />
                        )}
                    </div>
                    <SelectWithCategoriesLov
                        namedItems={fieldsToUpdateOptions}
                        currentItem={null}
                        initialState={null}
                        onChange={handleFieldToUpdateChange}
                        fieldSelect="fileToUpdate"
                        labelField=""
                        label={t('ra:fieldToUpdate')}
                        defaultText={t('ra:selectFieldToUpdate')}
                    />

                    <div className="dividing-line" />

                    {noFields.value && <span className="no-fields-have-been">{t('noFieldsSelected')}</span>}
                    {!noFields.value && (
                        <div>
                            {/* VISIT fields */}
                            {isFieldEnabled('active') && (
                                <div className={cn('update-field-wrapper', errors.active ? 'mb-30' : 'mb-20')}>
                                    <FormControl variant="outlined" className="form-control-type">
                                        <SelectWithCategories
                                            label={t('admin:Active')}
                                            dataArray={[...Constants.booleanSelector]}
                                            onChange={handleChange}
                                            fieldSelect="active"
                                            currentState={fieldsState.active}
                                            labelField="active"
                                            showWarning={false}
                                            filterable={false}
                                            customLabelId="edit-visit-active-label"
                                            customId="edit-visit-active"
                                            error={errors.active}
                                        />
                                    </FormControl>
                                    <DeleteButton onClick={() => deleteField('active')} />
                                </div>
                            )}
                            {isFieldEnabled('type') && (
                                <div className="update-field-wrapper">
                                    <FormControl variant="outlined" className="form-control-type">
                                        <TextField
                                            label={`${t('admin:visitType')}`}
                                            id="edit-visit-type"
                                            style={{ marginBottom: 10 }}
                                            fullWidth
                                            value={fieldsState.additionalInformation}
                                            onChange={handleChange('type', 60)}
                                            placeholder={t('admin:enterVisitTypeLabel')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="edit-visit-dialog-name"
                                                    length={fieldsState.type?.length}
                                                    max={60}
                                                    error={errors.type}
                                                />
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            className="mb"
                                            error={!!errors.type}
                                        />
                                    </FormControl>
                                    <DeleteButton onClick={() => deleteField('type')} />
                                </div>
                            )}
                            {isFieldEnabled('leadVisitId') && (
                                <div className={cn('update-field-wrapper', errors.leadVisitId ? 'mb-30' : 'mb-20')}>
                                    <FormControl variant="outlined" className="form-control-type">
                                        <LeadVisitIdField
                                            visits={
                                                visits?.filter(
                                                    (x) =>
                                                        itemsToUpdateArray.findIndex((el: any) => el.id === x.id) ===
                                                        -1,
                                                ) ?? []
                                            }
                                            onChange={handleChange}
                                            currentState={fieldsState.leadVisitId}
                                            error={errors.leadVisitId}
                                        />
                                    </FormControl>
                                    <DeleteButton
                                        onClick={() => {
                                            if (leadVisitIdTouched.value) {
                                                leadVisitIdTouched.setFalse();
                                            }
                                            deleteField('leadVisitId');
                                        }}
                                    />
                                </div>
                            )}
                            {isFieldEnabled('leadUnit') && (
                                <div className={cn('update-field-wrapper', errors.leadUnit ? 'mb-30' : 'mb-20')}>
                                    <FormControl variant="outlined" className="form-control-type">
                                        <SelectWithCategories
                                            showWarning={false}
                                            customClassName="lead-unit"
                                            fieldSelect="leadUnit"
                                            onChange={handleChange}
                                            label={`${t('admin:leadUnit')}`}
                                            filterable={false}
                                            labelField="leadUnit"
                                            dataArray={unitOptions}
                                            customId="add-select-lead-unit"
                                            currentState={fieldsState.leadUnit}
                                            error={errors.leadUnit}
                                        />
                                    </FormControl>
                                    <DeleteButton onClick={() => deleteField('leadUnit')} />
                                </div>
                            )}
                            {isFieldEnabled('leadPeriod') && (
                                <div className="update-field-wrapper mb-10">
                                    <FormControl variant="outlined" className="form-control-type">
                                        <TextField
                                            label={`${t('admin:leadTime')}`}
                                            id="add-lead-time"
                                            fullWidth
                                            type="number"
                                            value={fieldsState.leadPeriod || ''}
                                            onKeyDown={handleChange('leadPeriod', 6, true)}
                                            onChange={handleChange('leadPeriod', 6, true)}
                                            placeholder={t('admin:enterLeadTime')}
                                            variant="outlined"
                                            color="secondary"
                                            error={!!errors.leadPeriod}
                                            helperText={
                                                <CharactersRemaining
                                                    id="add-lead-time-help-text"
                                                    error={errors.leadPeriod}
                                                    customText={t('common:enterNumericValue')}
                                                />
                                            }
                                        />
                                    </FormControl>
                                    <DeleteButton onClick={() => deleteField('leadPeriod')} />
                                </div>
                            )}
                            {isFieldEnabled('minWinPeriod') && (
                                <div className="update-field-wrapper mb-10">
                                    <FormControl variant="outlined" className="form-control-type">
                                        <TextField
                                            label={`${t('admin:minWinTime')}`}
                                            id="add-min-win-time"
                                            fullWidth
                                            type="number"
                                            value={fieldsState.minWinPeriod || ''}
                                            onKeyDown={handleChange('minWinPeriod', 6, true)}
                                            onChange={handleChange('minWinPeriod', 6, true)}
                                            placeholder={t('admin:enterMinWinTimeLabel')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="visit-min-win-time-help-text"
                                                    error={errors.minWinPeriod}
                                                    customText={t('common:enterNumericValue')}
                                                />
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!errors.minWinPeriod}
                                        />
                                    </FormControl>
                                    <DeleteButton onClick={() => deleteField('minWinPeriod')} />
                                </div>
                            )}
                            {isFieldEnabled('maxWinPeriod') && (
                                <div className="update-field-wrapper mb-10">
                                    <FormControl variant="outlined" className="form-control-type">
                                        <TextField
                                            label={`${t('admin:maxWinTime')}`}
                                            id="add-max-win-time"
                                            fullWidth
                                            type="number"
                                            value={fieldsState.maxWinPeriod || ''}
                                            onKeyDown={handleChange('maxWinPeriod', 6, true)}
                                            onChange={handleChange('maxWinPeriod', 6, true)}
                                            placeholder={t('admin:enterMaxWinTimeLabel')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="visit-max-win-time-help-text"
                                                    error={errors.maxWinPeriod}
                                                    customText={t('common:enterNumericValue')}
                                                />
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!errors.maxWinPeriod}
                                        />
                                    </FormControl>
                                    <DeleteButton onClick={() => deleteField('maxWinPeriod')} />
                                </div>
                            )}
                            {isFieldEnabled('minWinUnit') && (
                                <div className={cn('update-field-wrapper', errors.minWinUnit ? 'mb-30' : 'mb-20')}>
                                    <FormControl variant="outlined" className="form-control-type">
                                        <SelectWithCategories
                                            showWarning={false}
                                            customClassName="min-max-unit"
                                            fieldSelect="minWinUnit"
                                            onChange={handleChange}
                                            label={`${t('admin:minMaxUnit')}`}
                                            filterable={false}
                                            labelField="minWinUnit"
                                            dataArray={unitOptions}
                                            customId="add-min-max-unit"
                                            currentState={fieldsState.minWinUnit}
                                            error={errors.minWinUnit}
                                        />
                                    </FormControl>
                                    <DeleteButton onClick={() => deleteField('minWinUnit')} />
                                </div>
                            )}
                            {isFieldEnabled('minOccurrences') && (
                                <div className="update-field-wrapper">
                                    <FormControl variant="outlined" className="form-control-type">
                                        <TextField
                                            label={t('admin:numberOccurrencesGridLabel')}
                                            id="add-visit-occurrences"
                                            type="number"
                                            value={fieldsState.minOccurrences || ''}
                                            onKeyDown={handleChange('minOccurrences', 6, true)}
                                            onChange={handleChange('minOccurrences', 6, true)}
                                            placeholder={t('admin:enterNumberOccurrences')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="visit-minOccurrences-help-text"
                                                    error={errors.minOccurrences}
                                                    customText={t('common:enterNumericValue')}
                                                />
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!errors.minOccurrences}
                                        />
                                    </FormControl>
                                    <DeleteButton onClick={() => deleteField('minOccurrences')} />
                                </div>
                            )}
                            {isFieldEnabled('forecastEndDate') && (
                                <div
                                    className={cn(
                                        'update-field-wrapper align-end',
                                        errors.forecastEndDate ? 'mb-30' : 'mb-20',
                                    )}
                                >
                                    <FormControl variant="outlined" className="form-control-type">
                                        <ForecastEndDateField
                                            currentState={fieldsState.forecastEndDate}
                                            error={errors.forecastEndDate}
                                            isRequired={false}
                                            onChange={handleChange}
                                        />
                                    </FormControl>
                                    <DeleteButton onClick={() => deleteField('forecastEndDate')} />
                                </div>
                            )}
                            {isFieldEnabled('endDateUsage') && (
                                <div className={cn('update-field-wrapper', errors.endDateUsage ? 'mb-30' : 'mb-20')}>
                                    <FormControl variant="outlined" className="form-control-type">
                                        <SelectWithCategories
                                            showWarning={false}
                                            customClassName="end-date-usage"
                                            fieldSelect="endDateUsage"
                                            onChange={handleChange}
                                            label={`${t('admin:endDateUsage')}`}
                                            filterable={false}
                                            labelField="endDateUsage"
                                            dataArray={endDateUsage}
                                            customId="add-endDateUsage"
                                            currentState={fieldsState.endDateUsage}
                                            error={errors.endDateUsage}
                                        />
                                    </FormControl>
                                    <DeleteButton onClick={() => deleteField('endDateUsage')} />
                                </div>
                            )}
                        </div>
                    )}

                    {visitsLoading && <Loading float />}
                </DialogContent>
                <DialogActions className="actions-wrapper">
                    <SaveButton
                        id="edit-multiple-risks-save"
                        text="common:update"
                        disabled={disabledSave.value || !!checkForErrors(errors)}
                        onClick={() => {
                            validateSubmit();
                        }}
                    />
                </DialogActions>
            </CustomDialog>
        </>
    );
};

export default EditMultipleFieldsModal;
